import React, { useState } from 'react';
import { simStudioApi } from '../api/simStudioApi';
import type { Theme } from './StudioHeader';
import { IconPlay, IconRefresh } from './Icons';

interface Props {
  escolaId: string | null;
  onSeedDone?: () => void;
  theme: Theme;
}

export function ScenarioControls({ escolaId, onSeedDone, theme }: Props) {
  const isDark = theme === 'dark';
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveMsg, setLiveMsg] = useState<string | null>(null);
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedMsg, setSeedMsg] = useState<string | null>(null);

  const labelColor = isDark ? '#475569' : '#94a3b8';
  const divider    = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const msgBg      = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
  const msgColor   = isDark ? '#94a3b8' : '#64748b';

  const greenAccent = isDark ? '#4ade80' : '#16a34a';
  const redAccent   = isDark ? '#f87171' : '#dc2626';
  const greenRgb    = isDark ? '74,222,128' : '22,163,74';
  const redRgb      = isDark ? '248,113,113' : '220,38,38';

  const handleActivateLive = async () => {
    if (!escolaId) return;
    setLiveLoading(true); setLiveMsg(null);
    try {
      await simStudioApi.activateLiveScenario(escolaId);
      setLiveMsg('✓ Cenário ao vivo ativado! Novos eventos aparecerão no feed em breve.');
    } catch (e: any) {
      setLiveMsg(`Erro: ${e?.response?.data?.message ?? 'Falha ao ativar cenário'}`);
    } finally { setLiveLoading(false); }
  };

  const handleResetSeed = async () => {
    if (!window.confirm('Isso vai apagar e re-criar todos os dados demo. Continuar?')) return;
    setSeedLoading(true); setSeedMsg(null);
    try {
      await simStudioApi.executeDemoSeed(true);
      setSeedMsg('✓ Mega Seed executado com sucesso!');
      onSeedDone?.();
    } catch (e: any) {
      setSeedMsg(`Erro: ${e?.response?.data?.message ?? 'Falha no seed'}`);
    } finally { setSeedLoading(false); }
  };

  return (
    <div className="flex flex-col gap-2.5">
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: labelColor, paddingLeft: 2 }}>
        Controles de Cenário
      </div>

      {/* Ativar Cenário */}
      <button
        onClick={handleActivateLive}
        disabled={!escolaId || liveLoading}
        className="w-full flex items-center justify-center gap-2.5 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        style={{
          background: `rgba(${greenRgb},0.08)`,
          border: `1px solid rgba(${greenRgb},0.22)`,
          borderRadius: 10, padding: '11px 16px',
          color: greenAccent, fontSize: 13, fontWeight: 600,
        }}
      >
        {liveLoading
          ? <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: greenAccent, borderTopColor: 'transparent' }} />
          : <IconPlay size={14} color={greenAccent} />
        }
        {liveLoading ? 'Ativando...' : 'Ativar Cenário Ao Vivo'}
      </button>

      {liveMsg && (
        <div style={{ background: msgBg, borderRadius: 8, padding: '8px 12px', fontSize: 12, color: msgColor, lineHeight: 1.5 }}>
          {liveMsg}
        </div>
      )}

      <div style={{ borderTop: `1px solid ${divider}` }} />

      {/* Reset Seed */}
      <button
        onClick={handleResetSeed}
        disabled={seedLoading}
        className="w-full flex items-center justify-center gap-2.5 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        style={{
          background: `rgba(${redRgb},0.06)`,
          border: `1px solid rgba(${redRgb},0.16)`,
          borderRadius: 10, padding: '10px 16px',
          color: redAccent, fontSize: 12, fontWeight: 500,
        }}
      >
        {seedLoading
          ? <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: redAccent, borderTopColor: 'transparent' }} />
          : <IconRefresh size={14} color={redAccent} />
        }
        {seedLoading ? 'Executando seed...' : 'Resetar Mega Seed'}
      </button>

      {seedMsg && (
        <div style={{ background: msgBg, borderRadius: 8, padding: '8px 12px', fontSize: 12, color: msgColor, lineHeight: 1.5 }}>
          {seedMsg}
        </div>
      )}
    </div>
  );
}
